import { useEffect, useState } from 'react';
import { AssetGroup, AssetGroupInput, AssetGroupResponse } from '@/types/schemas/nhom-tscd.type';
import { useAuth } from '@/contexts/auth-context';
import api from '@/lib/api';

interface UseAssetGroupReturn {
  assetGroups: AssetGroup[];
  isLoading: boolean;
  addAssetGroup: (newAssetGroup: AssetGroupInput) => Promise<AssetGroup>;
  updateAssetGroup: (uuid: string, updatedAssetGroup: AssetGroupInput) => Promise<AssetGroup>;
  deleteAssetGroup: (uuid: string) => Promise<void>;
  refreshAssetGroups: (groupType?: string) => Promise<void>;
  fetchAssetGroupsByType: (groupType: string) => Promise<void>;
}

/**
 * Hook for managing AssetGroup data
 *
 * This hook provides functions to fetch, create, update, and delete asset groups.
 */
export const useAssetGroup = (initialAssetGroups: AssetGroup[] = []): UseAssetGroupReturn => {
  const [assetGroups, setAssetGroups] = useState<AssetGroup[]>(initialAssetGroups);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { entity } = useAuth();

  const fetchAssetGroups = async (groupType?: string) => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      // Get all groups or filter by group type if provided
      const url = groupType
        ? `/entities/${entity.slug}/erp/groups/?loai_nhom=${groupType}`
        : `/entities/${entity.slug}/erp/groups/`;

      const response = await api.get<AssetGroupResponse>(url);
      setAssetGroups(response.data.results);
    } catch (error) {
      console.error('Error fetching asset groups:', error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Fetch groups by type
   * @param groupType The group type to fetch
   */
  const fetchAssetGroupsByType = async (groupType: string) => {
    await fetchAssetGroups(groupType);
  };

  const addAssetGroup = async (newAssetGroup: AssetGroupInput): Promise<AssetGroup> => {
    if (!entity?.slug) throw new Error('Entity slug is required');

    setIsLoading(true);
    try {
      const payload = {
        ma_nhom: newAssetGroup.ma_nhom,
        ten_phan_nhom: newAssetGroup.ten_phan_nhom,
        ten2: newAssetGroup.ten2,
        trang_thai: newAssetGroup.trang_thai,
        loai_nhom: newAssetGroup.loai_nhom || 'TS'
      };

      const response = await api.post<AssetGroup>(`/entities/${entity.slug}/erp/groups/`, payload);
      const addedAssetGroup = response.data;

      setAssetGroups(prev => [...prev, addedAssetGroup]);
      return addedAssetGroup;
    } catch (error) {
      console.error('Error adding asset group:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateAssetGroup = async (uuid: string, updatedAssetGroup: AssetGroupInput): Promise<AssetGroup> => {
    if (!entity?.slug) throw new Error('Entity slug is required');

    setIsLoading(true);
    try {
      const payload = {
        ma_nhom: updatedAssetGroup.ma_nhom,
        ten_phan_nhom: updatedAssetGroup.ten_phan_nhom,
        ten2: updatedAssetGroup.ten2,
        trang_thai: updatedAssetGroup.trang_thai,
        loai_nhom: updatedAssetGroup.loai_nhom || 'TS'
      };

      const response = await api.put<AssetGroup>(`/entities/${entity.slug}/erp/groups/${uuid}/`, payload);
      const updatedAssetGroupData = response.data;

      setAssetGroups(prev =>
        prev.map(assetGroup => (assetGroup.uuid === updatedAssetGroupData.uuid ? updatedAssetGroupData : assetGroup))
      );

      return updatedAssetGroupData;
    } catch (error) {
      console.error('Error updating asset group:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteAssetGroup = async (uuid: string): Promise<void> => {
    if (!entity?.slug) throw new Error('Entity slug is required');

    setIsLoading(true);
    try {
      await api.delete(`/entities/${entity.slug}/erp/groups/${uuid}/`);
      setAssetGroups(prev => prev.filter(assetGroup => assetGroup.uuid !== uuid));
    } catch (error) {
      console.error('Error deleting asset group:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const refreshAssetGroups = async (groupType?: string): Promise<void> => {
    await fetchAssetGroups(groupType);
  };

  useEffect(() => {
    fetchAssetGroups();
  }, [entity?.slug]);

  return {
    assetGroups,
    isLoading,
    addAssetGroup,
    updateAssetGroup,
    deleteAssetGroup,
    refreshAssetGroups,
    fetchAssetGroupsByType
  };
};
